/**
 * analytics/ChartRenderer.jsx
 * Phase 2 Step 11 — Renders bar, pie, and line charts from analytics response data.
 * Uses recharts (responsive, fills available width).
 * Data format matches the output of backend analytics/formatter.py chart formatters.
 */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

/* ── Palette ─────────────────────────────────────────────────────────────── */
const COLORS = [
  '#3B82F6', // blue-500
  '#10B981', // emerald-500
  '#F59E0B', // amber-500
  '#EF4444', // red-500
  '#8B5CF6', // violet-500
  '#06B6D4', // cyan-500
  '#F97316', // orange-500
  '#EC4899', // pink-500
  '#84CC16', // lime-500
  '#6B7280', // gray-500
];

/* ── Bar Chart ───────────────────────────────────────────────────────────── */
const BarChartRenderer = ({ data, title }) => {
  const { labels = [], datasets = [], x_axis_label, y_axis_label } = data;

  // Defer render until the parent has been laid out so ResponsiveContainer
  // gets a real width on its first measurement (same fix as PieChartRenderer).
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setReady(true), 0);
    return () => clearTimeout(id);
  }, []);

  // Convert backend format to recharts format
  const chartData = labels.map((label, i) => {
    const entry = { [x_axis_label || 'x']: label };
    datasets.forEach((ds) => {
      entry[ds.label] = ds.data[i] ?? 0;
    });
    return entry;
  });

  const dataKeys = datasets.map((ds) => ds.label);

  return (
    <div className="w-full">
      {title && (
        <p className="text-sm font-semibold text-gray-700 mb-3 text-center">{title}</p>
      )}
      <div style={{ minHeight: 320 }}>
      {ready && (
      <ResponsiveContainer width="100%" height={320} debounce={50}>
        <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 60 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            dataKey={x_axis_label || 'x'}
            tick={{ fontSize: 11, fill: '#6B7280' }}
            angle={-40}
            textAnchor="end"
            interval={0}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#6B7280' }}
            label={
              y_axis_label
                ? { value: y_axis_label, angle: -90, position: 'insideLeft', fontSize: 11 }
                : undefined
            }
          />
          <Tooltip
            contentStyle={{ fontSize: 12, borderRadius: 8 }}
            labelStyle={{ fontWeight: 600 }}
          />
          {dataKeys.length > 1 && <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />}
          {dataKeys.map((key, i) => (
            <Bar key={key} dataKey={key} fill={COLORS[i % COLORS.length]} radius={[3, 3, 0, 0]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
      )}
      </div>
    </div>
  );
};

/* ── Pie Chart ───────────────────────────────────────────────────────────── */
const PieChartRenderer = ({ data, title }) => {
  const { labels = [], data: values = [] } = data;

  const chartData = labels.map((label, i) => ({
    name: label,
    value: typeof values[i] === 'number' ? values[i] : 0,
  }));

  // ResponsiveContainer measures its parent on mount and can get 0×0 when the
  // chat bubble hasn't finished laying out. Force a re-render after mount so
  // recharts gets a real width measurement.
  const containerRef = useRef(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setReady(true), 0);
    return () => clearTimeout(id);
  }, []);

  const RADIAN = Math.PI / 180;
  const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    if (percent < 0.04) return null;  // skip tiny slices
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div className="w-full" ref={containerRef}>
      {title && (
        <p className="text-sm font-semibold text-gray-700 mb-3 text-center">{title}</p>
      )}
      {/* min-h ensures the container has non-zero height before recharts measures */}
      <div style={{ minHeight: 320 }}>
        {ready && (
          <ResponsiveContainer width="100%" height={320} debounce={50}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                outerRadius={110}
                labelLine={false}
                label={renderLabel}
                dataKey="value"
              >
                {chartData.map((_, i) => (
                  <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, name) => [value.toLocaleString(), name]}
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
              />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

/* ── Line Chart ──────────────────────────────────────────────────────────── */
const LineChartRenderer = ({ data, title }) => {
  const { labels = [], datasets = [], x_axis_label, y_axis_label } = data;

  // Defer render until layout is measured — prevents 0×0 ResponsiveContainer.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setReady(true), 0);
    return () => clearTimeout(id);
  }, []);

  const chartData = labels.map((label, i) => {
    const entry = { [x_axis_label || 'x']: label };
    datasets.forEach((ds) => {
      entry[ds.label] = ds.data[i] ?? null;
    });
    return entry;
  });

  const dataKeys = datasets.map((ds) => ds.label);

  return (
    <div className="w-full">
      {title && (
        <p className="text-sm font-semibold text-gray-700 mb-3 text-center">{title}</p>
      )}
      <div style={{ minHeight: 320 }}>
      {ready && (
      <ResponsiveContainer width="100%" height={320} debounce={50}>
        <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 60 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            dataKey={x_axis_label || 'x'}
            tick={{ fontSize: 11, fill: '#6B7280' }}
            angle={-40}
            textAnchor="end"
            interval={0}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#6B7280' }}
            label={
              y_axis_label
                ? { value: y_axis_label, angle: -90, position: 'insideLeft', fontSize: 11 }
                : undefined
            }
          />
          <Tooltip
            contentStyle={{ fontSize: 12, borderRadius: 8 }}
            labelStyle={{ fontWeight: 600 }}
          />
          {dataKeys.length > 1 && <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />}
          {dataKeys.map((key, i) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={COLORS[i % COLORS.length]}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      )}
      </div>
    </div>
  );
};

/* ── Main ChartRenderer ──────────────────────────────────────────────────── */
const ChartRenderer = ({ outputType, data, title }) => {
  if (!data) return null;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      {outputType === 'bar_chart' && (
        <BarChartRenderer data={data} title={title} />
      )}
      {outputType === 'pie_chart' && (
        <PieChartRenderer data={data} title={title} />
      )}
      {outputType === 'line_chart' && (
        <LineChartRenderer data={data} title={title} />
      )}
    </div>
  );
};

/* ── PropTypes ───────────────────────────────────────────────────────────── */
ChartRenderer.propTypes = {
  outputType:     PropTypes.oneOf(['bar_chart', 'pie_chart', 'line_chart']).isRequired,
  data:           PropTypes.object.isRequired,
  title:          PropTypes.string,
};

ChartRenderer.defaultProps = {
  title: '',
};

export default ChartRenderer;
